<script>
  // The HQ offer itself (BLV specs/OFFER_CARD_SPEC.md): one offer record (map_offers) read by HQ, this builder and
  // the whiteboard. A company design shows every one of the company's offers (fn_bb_sync_offers), each through its
  // builder row (bb_deals.offer_id). An older row with no offer can still be linked or made into one. The name and
  // six boxes saved here show in HQ the next time it opens.
  import { workspace, refreshWorkspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';

  let { deal, onLinked = null } = $props();

  const BOXES = [
    ['promise_text', 'Promise', 'What is being given'],
    ['value_text', 'Value', 'The problem it solves, for whom, and why they’d buy'],
    ['niche_text', 'Niche', 'How it differs from the other choices'],
    ['how_text', 'Where / how', 'Where the offer is made and how it’s delivered']
  ];
  const ASKS = [['money', 'Money'], ['account', 'An account'], ['show_up', 'Show up'], ['time', 'Time'], ['data', 'Data']];

  let partnerId = $state(null), offers = $state([]), offer = $state(null), ready = $state(false), busy = $state(false);

  $effect(() => { if (workspace.id) load(workspace.id); });

  async function load(wsId) {
    const { data: ws } = await supabase.from('bb_workspaces').select('partner_id').eq('id', wsId).maybeSingle();
    partnerId = ws?.partner_id || null;
    if (partnerId) {
      const { data } = await supabase.from('map_offers').select('*').eq('partner_id', partnerId).neq('status', 'retired').order('rank');
      offers = data || [];
    }
    offer = offers.find((o) => o.id === deal?.offer_id) || null;
    ready = true;
  }

  async function link(offerId) {
    busy = true;
    const { data, error } = await supabase.from('bb_deals').update({ offer_id: offerId || null }).eq('id', deal.id).select('id, offer_id');
    busy = false;
    if (error || !data?.length) { showToast(error?.message || 'Not saved', 'error'); return; }
    offer = offers.find((o) => o.id === offerId) || null;
    onLinked?.(offerId || null);
  }

  async function makeOffer() {
    busy = true;
    const row = { partner_id: partnerId, name: deal.name || 'New offer', status: 'draft',
      promise_text: deal.result || null, how_text: deal.how_it_works || null,
      value_text: deal.what_it_is || null, ask_type: deal.price ? 'money' : null, ask_note: deal.price || null };
    const { data, error } = await supabase.from('map_offers').insert(row).select('*');
    busy = false;
    if (error || !data?.length) { showToast(error?.message || 'Could not make the offer', 'error'); return; }
    offers = [...offers, data[0]];
    await link(data[0].id);
  }

  async function save(field, raw) {
    if (!offer) return;
    let v = typeof raw === 'string' ? raw.trim() : raw;
    if (v === '') v = null;
    if ((field === 'cost_money' || field === 'cost_minutes') && v != null) v = Math.max(0, field === 'cost_minutes' ? Math.round(Number(v)) : Number(v)) || 0;
    if ((offer[field] ?? null) === v) return;
    if (field === 'name' && !v) return;
    const { data, error } = await supabase.from('map_offers').update({ [field]: v, updated_at: new Date().toISOString() }).eq('id', offer.id).select('*');
    // A 2xx with no row back means RLS refused it — never report that as saved.
    if (error || !data?.length) { showToast(error?.message || 'Not saved — you can’t edit this offer', 'error'); return; }
    offer = data[0];
    offers = offers.map((o) => (o.id === offer.id ? offer : o));
    if (field === 'name') {
      await supabase.from('bb_deals').update({ name: v }).eq('id', deal.id);
      await refreshWorkspace();
    }
  }
</script>

{#if ready && partnerId}
  <div class="hq">
    <div class="hq-head">
      <span class="hq-l">HQ offer</span>
      {#if !offer}
        <select value="" disabled={busy} onchange={(e) => link(e.currentTarget.value)}>
          <option value="">— not linked —</option>
          {#each offers as o (o.id)}<option value={o.id}>{o.name}</option>{/each}
        </select>
        <button type="button" disabled={busy} onclick={makeOffer}>Make it an HQ offer</button>
      {/if}
    </div>
    {#if offer}
      <p class="hq-note">This is the offer itself — the same one HQ and the board show.</p>
      <label class="hq-box"><span>Name</span>
        <input type="text" value={offer.name || ''} onblur={(e) => save('name', e.currentTarget.value)} />
      </label>
      {#each BOXES as [f, label, hint] (f)}
        <label class="hq-box"><span>{label}</span>
          <textarea rows="2" placeholder={hint} value={offer[f] || ''} onblur={(e) => save(f, e.currentTarget.value)}></textarea>
        </label>
      {/each}
      <div class="hq-row">
        <label class="hq-box"><span>Cost · $ per person</span><input type="number" min="0" step="0.01" value={offer.cost_money ?? ''} onblur={(e) => save('cost_money', e.currentTarget.value)} /></label>
        <label class="hq-box"><span>Minutes per person</span><input type="number" min="0" step="1" value={offer.cost_minutes ?? ''} onblur={(e) => save('cost_minutes', e.currentTarget.value)} /></label>
      </div>
      <div class="hq-box"><span>Ask · what they give in return</span>
        <div class="hq-asks">
          {#each ASKS as [k, l] (k)}<button type="button" class:on={offer.ask_type === k} onclick={() => save('ask_type', offer.ask_type === k ? null : k)}>{l}</button>{/each}
        </div>
        <input type="text" placeholder="A note: the price as written, what data, how long" value={offer.ask_note || ''} onblur={(e) => save('ask_note', e.currentTarget.value)} />
      </div>
    {/if}
  </div>
{/if}

<style>
  .hq { margin: 0 0 20px; padding: 14px; border-radius: 14px; border: 1px solid rgba(212,175,55,0.25); background: rgba(212,175,55,0.05); }
  .hq-head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  .hq-l { color: #d4af37; font-size: 12px; font-weight: 600; letter-spacing: 0.4px; text-transform: uppercase; }
  .hq select, .hq input, .hq textarea { background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; color: #ececf0; font: inherit; font-size: 13px; padding: 7px 10px; box-sizing: border-box; }
  .hq select { flex: 1; min-width: 160px; }
  .hq textarea, .hq-box input { width: 100%; resize: vertical; line-height: 1.5; }
  .hq button { background: rgba(212,175,55,0.1); border: 1px solid rgba(212,175,55,0.3); color: #d4af37; border-radius: 999px; padding: 6px 12px; font: inherit; font-size: 12px; cursor: pointer; }
  .hq-note { color: #9a9aa2; font-size: 12px; margin: 10px 0; }
  .hq-box { display: flex; flex-direction: column; gap: 5px; margin-bottom: 10px; flex: 1; min-width: 0; }
  .hq-box > span { color: #d8d8dc; font-size: 12px; font-weight: 500; }
  .hq-row { display: flex; gap: 10px; flex-wrap: wrap; }
  .hq-asks { display: flex; flex-wrap: wrap; gap: 6px; }
  .hq-asks button.on { background: #d4af37; color: #141216; border-color: #d4af37; }
</style>
